import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Linkedin } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import psLogo from '@/assets/ps-logo.png';

export function Footer() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { href: getLocalizedPath('/'), label: t('common.home') },
    { href: getLocalizedPath('/datafiler'), label: t('common.dataFiles') },
    { href: getLocalizedPath('/shop'), label: t('common.shop') },
    { href: getLocalizedPath('/om-oss'), label: t('common.about') },
    { href: getLocalizedPath('/kontakt'), label: t('common.contact') },
  ];

  const infoLinks = [
    { href: getLocalizedPath('/information/data-i-vara-tjanster'), label: t('navigation.dataInServices') },
    { href: getLocalizedPath('/information/postnummer'), label: t('navigation.postalCodes') },
    { href: getLocalizedPath('/information/kopvillkor'), label: t('navigation.terms') },
    { href: getLocalizedPath('/information/faq'), label: t('navigation.faq') },
  ];

  return (
    <footer className="bg-sidebar text-sidebar-foreground">
      <div className="container py-8 md:py-12">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {/* Company Info */}
          <div className="space-y-3 col-span-2 md:col-span-1">
            <span className="text-base md:text-lg font-bold text-sidebar-foreground">Postnummerservice</span>
            <p className="text-xs md:text-sm text-sidebar-foreground/80">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.linkedin.com/company/postnummerservice"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sidebar-foreground/70 hover:text-sidebar-primary transition-colors"
                aria-label="Postnummerservice på LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <nav aria-label={t('footer.quickLinks')} className="space-y-3">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-wider text-sidebar-foreground/60">
              {t('footer.quickLinks')}
            </p>
            <ul className="space-y-1.5 md:space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-xs md:text-sm text-sidebar-foreground/80 hover:text-sidebar-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Information */}
          <nav aria-label={t('common.information')} className="space-y-3">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-wider text-sidebar-foreground/60">
              {t('common.information')}
            </p>
            <ul className="space-y-1.5 md:space-y-2">
              {infoLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-xs md:text-sm text-sidebar-foreground/80 hover:text-sidebar-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact */}
          <div className="space-y-3 col-span-2 md:col-span-1">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-wider text-sidebar-foreground/60">
              {t('common.contact')}
            </p>
            <ul className="space-y-2 md:space-y-3">
              <li className="flex items-start space-x-2 md:space-x-3 text-xs md:text-sm text-sidebar-foreground/80">
                <MapPin className="h-3.5 w-3.5 md:h-4 md:w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Postnummerservice AB<br />
                  Box 1234<br />
                  111 22 Stockholm
                </span>
              </li>
              <li className="flex items-center space-x-2 md:space-x-3 text-xs md:text-sm text-sidebar-foreground/80">
                <Mail className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" />
                <a href="mailto:info@postnummerservice.se" className="hover:text-sidebar-primary transition-colors">
                  info@postnummerservice.se
                </a>
              </li>
              <li className="flex items-center space-x-2 md:space-x-3 text-xs md:text-sm text-sidebar-foreground/80">
                <Mail className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" />
                <a href="mailto:support@postnummerservice.se" className="hover:text-sidebar-primary transition-colors">
                  support@postnummerservice.se
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-sidebar-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
            <p className="text-xs md:text-sm text-sidebar-foreground/60">
              © {currentYear} Postnummerservice AB. {t('common.allRightsReserved')}.
            </p>
            <div className="flex space-x-4 md:space-x-6">
              <Link
                to={getLocalizedPath('/information/kopvillkor')}
                className="text-xs md:text-sm text-sidebar-foreground/60 hover:text-sidebar-primary transition-colors"
              >
                {t('navigation.terms')}
              </Link>
              <Link
                to={getLocalizedPath('/integritetspolicy')}
                className="text-xs md:text-sm text-sidebar-foreground/60 hover:text-sidebar-primary transition-colors"
              >
                {t('footer.privacyPolicy')}
              </Link>
              <Link
                to={getLocalizedPath('/cookiepolicy')}
                className="text-xs md:text-sm text-sidebar-foreground/60 hover:text-sidebar-primary transition-colors"
              >
                {t('footer.cookiePolicy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}